/**
 * Excel Utilities for Human Loss Reports
 * Data transformation and mapping functions
 */

/**
 * Get IST timestamp for filenames
 */
export const getTimestamp = () => {
  const now = new Date();
  const istTime = new Date(now.getTime() + (5.5 * 60 * 60 * 1000));
  const istDate = istTime.toISOString().split('T')[0];
  const istTimeStr = istTime.toISOString().split('T')[1].split('.')[0].replace(/:/g, '');
  return `${istDate}_${istTimeStr}`;
};

/**
 * Format date to DD-MM-YYYY
 */
const formatDateToDDMMYYYY = (dateStr) => {
  if (!dateStr) return '';
  if (dateStr.match(/^\d{2}-\d{2}-\d{4}$/)) return dateStr;
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateStr.split('-');
    return `${day}-${month}-${year}`;
  }
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

/**
 * Format time to HH:MM
 */
const formatTime = (timeStr) => {
  if (!timeStr) return 'NA';
  if (timeStr.includes(':')) {
    const parts = timeStr.split(':');
    return `${parts[0]}:${parts[1]}`;
  }
  return timeStr;
};

/**
 * Safe value getter - PRESERVE "NA" as text, blanks as blank
 */
const getSafeValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  const strVal = String(value).trim();
  if (strVal === '') {
    return '';
  }
  if (strVal === 'NA' || strVal === 'na') {
    return 'NA';
  }
  return strVal;
};

/**
 * Transform PRIMARY Report Data (10 columns)
 */
export const transformPrimaryReportData = (data) => {
  const columns = [
    { label: 'Date', key: 'date' },
    { label: 'Incident Time', key: 'incidentTime' },
    { label: 'Name of district', key: 'distName' },
    { label: 'Block', key: 'blockName' },
    { label: 'Name of Disaster', key: 'disasterName' },
    { label: 'Incident ID', key: 'incidentId' },
    { label: 'Latitude', key: 'latitude' },
    { label: 'Longitude', key: 'longitude' },
    { label: 'Number of deaths', key: 'numberOfDeaths' },
    { label: 'Number of injured', key: 'numberOfInjured' }
  ];

  const transformedData = data.map(row => ({
    'Date': formatDateToDDMMYYYY(row.reported_on || row.incidentDate || row.date),
    'Incident Time': formatTime(row.incidentTime),
    'Name of district': getSafeValue(row.distName || row.district_name),
    'Block': getSafeValue(row.blockName || row.block_name),
    'Name of Disaster': getSafeValue(row.disaster_name || row.disasterName),
    'Incident ID': getSafeValue(row.incidentId),
    'Latitude': getSafeValue(row.latitude),
    'Longitude': getSafeValue(row.longitude),
    'Number of deaths': getSafeValue(row.noOfCasualtyDeaths || row.numberOfDeaths || row.number_of_deaths || 0),
    'Number of injured': getSafeValue(row.noOfCasualtyInjured || row.numberOfInjured || row.number_of_injured || 0)
  }));

  return { columns, transformedData };
};

/**
 * Transform DETAILED Human Loss Report Data (27 columns)
 */
export const transformDetailedReportData = (data) => {
  const columns = [
    { label: 'S.NO.', key: 'sno' },
    { label: 'Date', key: 'reported_on' },
    { label: 'Incident Time', key: 'incidentTime' },
    { label: 'Name of district', key: 'distName' },
    { label: 'Block', key: 'blockName' },
    { label: 'Name of Disaster', key: 'nameOfDisaster' },
    { label: 'Incident ID', key: 'incidentId' },
    { label: 'Latitude', key: 'latitude' },
    { label: 'Longitude', key: 'longitude' },
    { label: 'Type of casualty', key: 'typeOfCasualty' },
    { label: 'Name of deceased', key: 'personName' },
    { label: "Father's name/husband's name", key: 'personFamilyMemberName' },
    { label: 'Age', key: 'age' },
    { label: 'Gender', key: 'gender' },
    { label: 'Residential address', key: 'personAddress' },
    { label: "Victim's activity", key: 'activityDuringAccident' },
    { label: 'Post mortem done', key: 'isPostMortem' },
    { label: 'Occupation', key: 'occupation' },
    { 
      label: 'Whether the death due to heatstroke has been certified by the competent authority/doctor',
      key: 'heatStrokeCertified'
    },
    { label: 'Hospitalized', key: 'isHospitalized' },
    { label: 'Hospital name', key: 'hospitalName' },
    { label: 'Reported to police station', key: 'isReportedToPS' },
    { label: 'Police station name', key: 'policeStationName' },
    { label: 'Remarks', key: 'remarks' },
    { label: 'Entry date', key: 'entryDate' },
    { label: 'Payment status', key: 'paymentStatus' },
    { label: 'Payment date', key: 'paymentDate' }
  ];

  const transformedData = data.map((row, index) => ({
    'S.NO.': index + 1,
    'Date': formatDateToDDMMYYYY(row.reported_on || row.incidentDate || row.date),
    'Incident Time': getSafeValue(row.incidentTime),
    'Name of district': getSafeValue(row.distName || row.district_name),
    'Block': getSafeValue(row.blockName || row.block_name),
    'Name of Disaster': getSafeValue(row.nameOfDisaster || row.disaster_name),
    'Incident ID': getSafeValue(row.incidentId),
    'Latitude': getSafeValue(row.latitude),
    'Longitude': getSafeValue(row.longitude),
    'Type of casualty': getSafeValue(row.typeOfCasualty),
    'Name of deceased': getSafeValue(row.personName),
    "Father's name/husband's name": getSafeValue(row.personFamilyMemberName),
    'Age': getSafeValue(row.age),
    'Gender': getSafeValue(row.gender),
    'Residential address': getSafeValue(row.personAddress),
    "Victim's activity": getSafeValue(row.activityDuringAccident),
    'Post mortem done': getSafeValue(row.isPostMortem),
    'Occupation': getSafeValue(row.occupation),
    'Whether the death due to heatstroke has been certified by the competent authority/doctor':
      getSafeValue(row.heatStrokeCertified),
    'Hospitalized': getSafeValue(row.isHospitalized),
    'Hospital name': getSafeValue(row.hospitalName),
    'Reported to police station': getSafeValue(row.isReportedToPS),
    'Police station name': getSafeValue(row.policeStationName),
    'Remarks': getSafeValue(row.remarks),
    'Entry date': formatDateToDDMMYYYY(row.entryDate),
    'Payment status': getSafeValue(row.paymentStatus),
    'Payment date': getSafeValue(formatDateToDDMMYYYY(row.paymentDate))
  }));

  return { columns, transformedData };
};

/**
 * Transform MIS District Report Data (EXACT WEB UI ORDER)
 */
export const transformMISDistrictReportData = (data) => {
  const columns = [
    { label: 'Date', key: 'reported_on' },
    { label: 'District', key: 'distName' },
    { label: 'Block Name', key: 'blockName' },
    { label: 'Name of Disaster', key: 'nameOfDisaster' },
    { label: 'Incident ID', key: 'incidentId' },
    { label: 'Latitude', key: 'latitude' },
    { label: 'Longitude', key: 'longitude' },
    { label: 'Type of casualty', key: 'typeOfCasualty' },
    { label: 'Name of deceased', key: 'personName' },
    { label: "Father's name/husband's name", key: 'personFamilyMemberName' },
    { label: 'Age', key: 'age' },
    { label: 'Gender', key: 'gender' },
    { label: 'Residential Address', key: 'personAddress' },
    { label: "Victim's activity", key: 'activityDuringAccident' },
    { label: 'Post mortem done', key: 'isPostMortem' },
    { label: 'occupation', key: 'occupation' },
    { label: 'Whether the death due to heatstroke has been certified by the competent authority/doctor.', key: 'heatStrokeCertified' },
    { label: 'Hospitalized', key: 'isHospitalized' },
    { label: 'Hospital name', key: 'hospitalName' },
    { label: 'Reported to police station', key: 'isReportedToPS' },
    { label: 'ploice station name', key: 'policeStationName' },
    { label: 'Remarks', key: 'remarks' },
    { label: 'Entry date', key: 'entryDate' },
    { label: 'Payment status', key: 'paymentStatus' },
    { label: 'Payment date', key: 'paymentDate' }
  ];

  const getYesNoValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const strVal = String(value).trim().toLowerCase();
    if (strVal === 'yes' || strVal === 'true') return 'Yes';
    if (strVal === 'no' || strVal === 'false') return 'No';
    if (strVal === 'na') return 'NA';
    return '-';
  };

  const getSafeValueWithDash = (value) => {
    if (value === null || value === undefined || String(value).trim() === '') {
      return '-';
    }
    const strVal = String(value).trim();
    return (strVal === 'NA' || strVal === 'na') ? 'NA' : strVal;
  };

  const transformedData = data.map((row, index) => ({
    'Date': formatDateToDDMMYYYY(row.reported_on),
    'District': getSafeValue(row.distName),
    'Block Name': getSafeValue(row.blockName),
    'Name of Disaster': getSafeValue(row.nameOfDisaster),
    'Incident ID': getSafeValue(row.incidentId),
    'Latitude': getSafeValue(row.latitude),
    'Longitude': getSafeValue(row.longitude),
    'Type of casualty': getSafeValue(row.typeOfCasualty),
    'Name of deceased': getSafeValue(row.personName),
    "Father's name/husband's name": getSafeValueWithDash(row.personFamilyMemberName),
    'Age': getSafeValue(row.age),
    'Gender': getSafeValue(row.gender),
    'Residential Address': getSafeValueWithDash(row.personAddress),
    "Victim's activity": getSafeValueWithDash(row.activityDuringAccident),
    'Post mortem done': getYesNoValue(row.isPostMortem),
    'occupation': getSafeValueWithDash(row.occupation),
    'Whether the death due to heatstroke has been certified by the competent authority/doctor.': getSafeValueWithDash(row.heatStrokeCertified),
    'Hospitalized': getYesNoValue(row.isHospitalized),
    'Hospital name': getSafeValueWithDash(row.hospitalName),
    'Reported to police station': getYesNoValue(row.isReportedToPS),
    'ploice station name': getSafeValueWithDash(row.policeStationName),
    'Remarks': getSafeValueWithDash(row.remarks),
    'Entry date': formatDateToDDMMYYYY(row.entryDate),
    'Payment status': getSafeValue(row.paymentStatus),
    'Payment date': formatDateToDDMMYYYY(row.paymentDate)
  }));

  return { columns, transformedData };
};
